import cn from 'classnames';
import { ComponentProps, HTMLAttributes, useMemo } from 'react';
import { numberWithSpaces } from '@lib/number-formatting';
import { Card } from '@ui/card';
import { Loader } from '@ui/loader';
import { MixNumberInput } from '@ui/mix-number-input';
import { IAgriculture, ICultureParamData } from '@@/agriculture/ts';
import { AgricultureParametersList } from '@@/agriculture/components/agriculture-parameters-list';

import styles from './index.module.sass';

export function SimpleMixingResultCard(props: ISimpleMixingCardProps) {
  const {
    className,
    profit,
    price,
    weight,
    cost,
    culture,
    disabled,
    params = [],
    disabledReason = '',
    loading = false,
    onPriceChange,
    ...rest
  } = props;
  const rootClass = cn(styles.root, className);

  const costFormatted = useMemo(() => numberWithSpaces(cost), [cost]);
  const currentProfit = useMemo(
    () => Math.abs(price <= 0 ? 0 : profit),
    [profit, price],
  );
  const currentProfitFormatted = useMemo(
    () => numberWithSpaces(currentProfit),
    [currentProfit],
  );

  return (
    <Card className={rootClass} {...rest}>
      {loading && <Loader />}
      {disabled && <div className={styles.placeholder}>{disabledReason}</div>}

      {!disabled && !loading && culture && (
        <>
          <div className={styles.header}>Результат расчета</div>

          <div className={styles.content}>
            <AgricultureParametersList culture={culture} params={params} />

            <div className={cn(styles.param, styles.weight)}>
              <span className={styles.param__label}>полученный вес (т)</span>
              <span className={styles.param__value}>{weight}</span>
            </div>
            <div className={styles.param}>
              <span className={styles.param__label}>стоимость</span>
              <MixNumberInput
                className={cn(styles.param__value, styles.param__input)}
                width="full"
                size="s"
                rightSide="₽/т"
                maxValue={10e5}
                value={price}
                onChange={onPriceChange}
              />
            </div>
            <div className={styles.param}>
              <span className={styles.param__label}>стоимость микса (руб)</span>
              <span className={styles.param__value}>
                <b>{costFormatted}</b>
              </span>
            </div>
          </div>

          <div
            className={cn([
              styles.profit,
              { [styles.profit_neutral]: price <= 0 },
              { [styles.profit_positive]: profit >= 0 },
              { [styles.profit_negative]: price > 0 && profit < 0 },
            ])}
          >
            <span>{price <= 0 || profit >= 0 ? 'выгода' : 'потери'} от миксования</span>
            <span className={styles.profit__value}>{currentProfitFormatted} руб.</span>
          </div>
        </>
      )}
    </Card>
  );
}

export interface ISimpleMixingCardProps
  extends Omit<ComponentProps<typeof Card>, 'children'> {
  weight: number;
  profit: number;
  price: number;
  cost: number;
  culture?: IAgriculture; // not culture when no selected bags
  loading?: boolean;
  params?: ICultureParamData[];
  disabled?: boolean;
  disabledReason?: string;
  onPriceChange?: (price: number) => void;
}
